import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  UserIcon, 
  PencilIcon,
  XMarkIcon,
  FolderIcon,
  MagnifyingGlassIcon,
  ArrowRightOnRectangleIcon,
  BookmarkIcon,
  PlusIcon,
  ListBulletIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { authAPI, projectAPI, exploreAPI, imageAPI, savedAPI } from '../services/api';
import ProjectCard from '../components/ProjectCard';
import SavedListModal from '../components/SavedListModal';
import { useMobile } from '../hooks/useMobile';
import { getImageUrl } from '../config/runtime';
import toast from 'react-hot-toast';

const UserProfile = () => {
  const { user: authUser, logout } = useAuth();
  const { isMobile } = useMobile();
  const { displayMode, setDisplayMode } = useTheme();
  const navigate = useNavigate();
  const [showProfileEditModal, setShowProfileEditModal] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const avatarInputRef = useRef(null);
  
  // Image style options
  const imageStyleOptions = [
    {
      value: "IKEA-style line drawings with simple black outlines",
      label: "IKEA Style",
      description: "Clean line drawings with simple black outlines",
      preview: "📐"
    },
    {
      value: "photorealistic photography style with natural lighting and realistic details",
      label: "Photorealistic",
      description: "Realistic photography with natural lighting",
      preview: "📷"
    },
    {
      value: "technical illustration style with clean lines and minimal shading",
      label: "Technical Illustration",
      description: "Clean technical drawings with minimal shading",
      preview: "📊"
    },
    {
      value: "realistic watercolor painting style with soft edges",
      label: "Watercolor",
      description: "Soft watercolor paintings with gentle edges",
      preview: "🎨"
    },
    {
      value: "modern flat design with bold colors and geometric shapes",
      label: "Modern Flat Design",
      description: "Bold colors and geometric shapes",
      preview: "🔷"
    },
    {
      value: "botanical illustration style with detailed plant anatomy and scientific accuracy",
      label: "Botanical Illustration",
      description: "Scientific plant drawings with detailed anatomy",
      preview: "🌿"
    },
    {
      value: "field guide sketch style with hand-drawn observations and annotations",
      label: "Field Guide Style",
      description: "Hand-drawn sketches like a naturalist's field notes",
      preview: "📝"
    },
    {
      value: "nature documentary style with cinematic composition and earthy tones",
      label: "Nature Documentary",
      description: "Cinematic nature photography with earthy tones",
      preview: "🎬"
    },
    {
      value: "vintage natural history illustration style with aged paper texture",
      label: "Vintage Natural History",
      description: "Classic scientific illustrations with aged appearance",
      preview: "📜"
    },
    {
      value: "minimalist nature sketch style with clean lines and organic shapes",
      label: "Minimalist Nature",
      description: "Simple, clean sketches emphasizing organic forms",
      preview: "🌱"
    }
  ];

  // Display mode options
  const displayModeOptions = [
    {
      value: "system",
      label: "System Default",
      description: "Follows your device settings",
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      )
    },
    {
      value: "light",
      label: "Light Mode",
      description: "Always use light theme",
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      )
    },
    {
      value: "dark",
      label: "Dark Mode",
      description: "Always use dark theme",
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
        </svg>
      )
    }
  ];

  const [activeTab, setActiveTab] = useState('saved'); // Only 'saved' tab remains
  const [isImageStyleDropdownOpen, setIsImageStyleDropdownOpen] = useState(false);
  const imageStyleDropdownRef = useRef(null);
  const queryClient = useQueryClient();

  // Fetch current user profile
  const { data: userResponse, isLoading, error } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => authAPI.getProfile(),
    initialData: authUser ? { data: authUser } : undefined
  });

  const user = userResponse?.data || authUser;

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: (profileData) => authAPI.updateProfile(profileData),
    onSuccess: (response) => {
      queryClient.setQueryData(['currentUser'], response);
      setShowProfileEditModal(false);
      setProfileForm({});
      toast.success('Profile updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update profile');
      console.error('Profile update error:', error);
    }
  });

  // State for search functionality
  const [searchTerm, setSearchTerm] = useState('');

  // Saved lists functionality
  const [listModal, setListModal] = useState({ isOpen: false, list: null, mode: 'create' });

  // Fetch saved lists with infinite query
  const { 
    data: savedListsData,
    isLoading: isSavedListsLoading,
    isFetchingNextPage: isFetchingSavedNext,
    hasNextPage: hasSavedNext,
    fetchNextPage: fetchSavedNext,
    error: savedListsError,
    refetch: refetchSavedLists
  } = useInfiniteQuery({
    queryKey: ['saved-lists-profile'],
    queryFn: ({ pageParam = 0 }) => savedAPI.getLists({
      offset: pageParam,
      limit: 20,
      include_items: false
    }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const pagination = lastPage?.data?.data?.pagination;
      const lastPageLists = lastPage?.data?.data?.lists || [];
      const requestedLimit = 20;
      
      // Stop immediately if we got an empty page
      if (lastPageLists.length === 0) {
        return undefined;
      }
      
      if (lastPageLists.length < requestedLimit) {
        return undefined;
      }
      
      if (pagination && pagination.hasMore === false) {
        return undefined;
      }
      
      // Safety check: stop after 10 pages
      const totalPages = allPages.length;
      if (totalPages >= 10) {
        return undefined;
      }
      
      if (pagination && pagination.hasMore === true && lastPageLists.length === requestedLimit) {
        const totalLists = allPages.reduce((total, page) => total + (page?.data?.data?.lists?.length || 0), 0);
        return totalLists;
      }
      
      return undefined;
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!authUser && activeTab === 'saved'
  });

  // Flatten all pages into a single lists array
  const savedLists = savedListsData?.pages.reduce((acc, page) => {
    return [...acc, ...(page?.data?.data?.lists || [])];
  }, []) || [];

  // Intersection observer ref for infinite scroll
  const observerTarget = useRef(null);

  // Intersection Observer for infinite scroll (only for saved lists now)
  const observeLastProject = useCallback(
    (node) => {
      if (isSavedListsLoading || isFetchingSavedNext || !hasSavedNext) return;
      if (observerTarget.current) observerTarget.current.disconnect();
      
      observerTarget.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasSavedNext && !isFetchingSavedNext) {
          fetchSavedNext();
        }
      }, {
        threshold: 0.1,
      });
      
      if (node) observerTarget.current.observe(node);
    },
    [isSavedListsLoading, isFetchingSavedNext, hasSavedNext, fetchSavedNext]
  );



  const handleEditProfile = () => {
    setProfileForm({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      avatar: user?.avatar || '',
      interests: user?.interests || '',
      app_settings: user?.app_settings || {}
    });
    setShowProfileEditModal(true);
  };

  const handleSignOut = async () => {
    try {
      await logout();
      toast.success('Signed out successfully');
    } catch (error) {
      toast.error('Failed to sign out');
      console.error('Sign out error:', error);
    }
  };

  const handleProfileFormSubmit = (e) => {
    e.preventDefault();
    updateProfileMutation.mutate(profileForm);
  };

  const handleProfileInputChange = (field, value) => {
    setProfileForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAppSettingsChange = (key, value) => {
    setProfileForm(prev => ({
      ...prev,
      app_settings: {
        ...prev.app_settings,
        [key]: value
      }
    }));
  };

  const getCurrentImageStyleOption = () => {
    const currentStyle = profileForm.app_settings?.image_style || 'IKEA-style line drawings with simple black outlines';
    return imageStyleOptions.find(option => option.value === currentStyle) || imageStyleOptions[0];
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (imageStyleDropdownRef.current && !imageStyleDropdownRef.current.contains(event.target)) {
        setIsImageStyleDropdownOpen(false);
      }
    };

    if (isImageStyleDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isImageStyleDropdownOpen]);

  // Saved lists handlers
  const handleCreateList = () => {
    setListModal({ isOpen: true, list: null, mode: 'create' });
  };

  const closeListModal = () => {
    setListModal({ isOpen: false, list: null, mode: 'create' });
  };

  const handleViewList = (list) => {
    navigate(`/saved/${list.id}`, {
      state: {
        previousPath: '/profile',
        transitionDirection: 'forward'
      }
    });
  };

  const handleAvatarFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try {
      setIsUploadingAvatar(true);
      const fd = new FormData();
      fd.append('image', file);
      const res = await imageAPI.upload(fd);
      const imageUrl = res?.data?.data?.url;
      if (imageUrl) {
        setProfileForm(prev => ({ ...prev, avatar: imageUrl }));
        toast.success('Image uploaded');
      } else {
        toast.error('Upload failed');
      }
    } catch (err) {
      console.error('Avatar upload error:', err);
      toast.error('Failed to upload image');
    } finally {
      setIsUploadingAvatar(false);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  const getDisplayName = (user) => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    } else if (user?.firstName) {
      return user.firstName;
    } else if (user?.username) {
      return user.username;
    }
    return 'User';
  };

  const getInitials = (user) => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    } else if (user?.firstName) {
      return user.firstName[0].toUpperCase();
    } else if (user?.username) {
      return user.username[0].toUpperCase();
    }
    return 'U';
  };




  const closeModals = () => {
    setShowProfileEditModal(false);
    setProfileForm({});
  };

  // Animation variants for bottom slide (mobile) and fade/scale (desktop)
  const modalVariants = {
    hidden: isMobile ? {
      y: '100%',
      opacity: 0,
    } : {
      y: 20,
      opacity: 0,
      scale: 0.95,
    },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
    },
    exit: isMobile ? {
      y: '100%',
      opacity: 0,
    } : {
      y: 20,
      opacity: 0,
      scale: 0.95,
    },
  };

  // Background variants for fade effect
  const backgroundVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 },
  };

  // Animation transition settings  
  const transition = {
    type: 'tween',
    ease: [0.4, 0, 0.2, 1],
    duration: isMobile ? 0.35 : 0.25,
  };

  const backgroundTransition = {
    type: 'tween',
    ease: 'easeOut',
    duration: 0.2,
  };

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="px-4 py-6 sm:px-0">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-8"></div>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow h-96"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="px-4 py-6 sm:px-0">
            <div className="text-center">
              <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h2 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">Profile Error</h2>
              <p className="mt-2 text-gray-600 dark:text-gray-300">
                Unable to load your profile. Please try again.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8 pt-0">
        <div className="px-4 pt-6 pb-24 md:pb-6 sm:px-0">

        {/* Profile Header */}
        <div className="pb-6">
          {/* User Info Section */}
          <div className="flex flex-col sm:flex-row sm:items-start gap-4 mb-6">
            {/* Avatar and Basic Info */}
            <div className="flex items-center gap-4">
              <div className="h-20 w-20 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                {user?.avatar ? (
                  <img src={getImageUrl(user.avatar)} alt="" className="h-20 w-20 rounded-full object-cover" />
                ) : (
                  <span className="text-white font-bold text-2xl">{getInitials(user)}</span>
                )}
              </div>
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">{getDisplayName(user)}</h1>
                <p className="text-lg text-gray-600 dark:text-gray-300">@{user?.username}</p>
                {user?.createdAt && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Member since {new Date(user.createdAt).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 sm:gap-2 sm:ml-auto overflow-x-auto">
              <Link
                to={`/users/${user?.username}`}
                className="inline-flex gap-2 items-center px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors rounded-md text-sm whitespace-nowrap"
              >
                <EyeIcon className="h-4 w-4 flex-shrink-0" />
                <span className="hidden sm:inline">View Profile</span>
                <span className="sm:hidden">Public</span>
              </Link>
              <button
                onClick={handleEditProfile}
                className="inline-flex gap-2 items-center px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors rounded-md text-sm whitespace-nowrap"
              >
                <PencilIcon className="h-4 w-4 flex-shrink-0" />
                <span className="hidden sm:inline">Edit Profile</span>
                <span className="sm:hidden">Edit</span>
              </button>
              <button
                onClick={handleSignOut}
                className="inline-flex gap-2 items-center px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 hover:border-red-300 dark:hover:border-red-600 hover:text-red-600 dark:hover:text-red-400 transition-colors rounded-md text-sm whitespace-nowrap"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4 flex-shrink-0" />
                <span className="hidden sm:inline">Sign Out</span>
                <span className="sm:hidden">Sign Out</span>
              </button>
            </div>
          </div>

          {/* User Stats */}
          {/* <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{savedLists.length}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Saved Lists</div>
            </div>
            <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{userCompletedProjects.length}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Completed</div>
            </div>
            <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{userCreatedProjects.length}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Created</div>
            </div>
          </div> */}
          
          {/* Description - Full Width */}
          {/* {user?.interests && (
            <div className="mt-4 max-w-3xl">
              <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-line">
                {user.interests}
              </p>
            </div>
          )} */}
        </div>

        {/* Tabs and Projects Section */}
        <div className="mt-2">

          {/* Tabs Header */}
          <div className="flex items-center gap-4 sm:gap-8 border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
            <button
              onClick={() => setActiveTab('saved')}
              className={`pb-2 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                activeTab === 'saved'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <span className="hidden sm:inline">Saved Projects</span>
              <span className="sm:hidden">Saved</span>
            </button>
            
          </div>

          {/* Search */}
          {activeTab !== 'saved' && (
            <div className="my-6">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search projects..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
            </div>
          )}

          {/* Content */}
          {(
            /* Saved Lists */
            <>
              {isSavedListsLoading && savedLists.length === 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 animate-pulse">
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full mb-4"></div>
                      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                    </div>
                  ))}
                </div>
              ) : savedListsError ? (
                <div className="text-center py-12">
                  <div className="text-red-500 dark:text-red-400 mb-4">
                    Error loading saved lists
                  </div>
                  <button 
                    onClick={() => refetchSavedLists()}
                    className="btn-primary"
                  >
                    Retry
                  </button>
                </div>
              ) : savedLists.length > 0 ? (
                <>
                  {/* Create New List Button */}
                  <div className="border-b border-gray-200 dark:border-gray-700">
                    <button
                      onClick={handleCreateList}
                      className="w-full flex items-center gap-4 p-4 hover:bg-gray-100 dark:hover:bg-gray-800 text-left"
                    >
                      <div className="w-12 h-12 bg-gray-700 dark:bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                        <PlusIcon className="w-6 h-6 text-gray-50 dark:text-gray-600" />
                      </div>
                      <div>
                        <div className="text-lg font-medium text-gray-900 dark:text-white">
                          Create new list
                        </div>
                      </div>
                    </button>
                  </div>

                  {/* Lists */}
                  <div className="space-y-0">
                    {savedLists.map((list, index) => {
                      const isNearEnd = index >= savedLists.length - 3;
                      
                      return (
                        <div
                          key={list.id}
                          ref={isNearEnd ? observeLastProject : null}
                          className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-shadow duration-200"
                        >
                          <div className="flex items-center justify-between p-4">
                            <button
                              onClick={() => handleViewList(list)}
                              className="flex items-center gap-4 flex-1 text-left min-w-0 overflow-hidden"
                            >
                              <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-xl flex items-center justify-center flex-shrink-0">
                                <BookmarkIcon className="w-6 h-6 text-gray-600 dark:text-gray-400" />
                              </div>
                              <div className="flex-1 min-w-0 overflow-hidden">
                                <div className="flex items-center gap-2 mb-1 min-w-0">
                                  <h3 className="text-lg font-medium text-gray-900 dark:text-white truncate flex-1 min-w-0" title={list.name}>
                                    {list.name}
                                  </h3>
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    {list.isDefault && (
                                      <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded whitespace-nowrap">
                                        Default
                                      </span>
                                    )}
                                    {!list.isPublic && (
                                      <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                      </svg>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 min-w-0">
                                  <span className="flex-shrink-0">{list.itemCount || 0} item{(list.itemCount || 0) !== 1 ? 's' : ''}</span>
                                  {list.description && (
                                    <span className="truncate flex-1 min-w-0" title={list.description}>{list.description}</span>
                                  )}
                                </div>
                              </div>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Infinite scroll loading spinner */}
                  {isFetchingSavedNext && (
                    <div className="flex justify-center items-center py-8">
                      <div className="flex items-center space-x-2">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 dark:border-blue-400"></div>
                        <span className="text-gray-600 dark:text-gray-400 text-sm">Loading more lists...</span>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-16">
                  <div className="mx-auto w-24 h-24 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-6">
                    <ListBulletIcon className="h-12 w-12 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    No saved lists yet
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-8 max-w-sm mx-auto">
                    Create your first list to organize and save your favorite projects for easy access.
                  </p>
                  <button
                    onClick={handleCreateList}
                    className="inline-flex items-center px-6 py-3 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  >
                    <PlusIcon className="h-5 w-5 mr-2" />
                    Create Your First List
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Profile Edit Modal */}
        <AnimatePresence>
          {showProfileEditModal && (
            <motion.div 
              className={`fixed inset-0 bg-black bg-opacity-50 z-[60000] ${
                isMobile 
                  ? 'flex items-stretch justify-center' 
                  : 'flex items-center justify-center p-4'
              }`}
              variants={backgroundVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={backgroundTransition}
              onClick={closeModals}
              style={{ isolation: 'isolate', contain: 'layout style paint' }}
            >
            <motion.div 
              className={`bg-white dark:bg-gray-800 w-full flex flex-col overflow-hidden relative z-[60001] ${
                isMobile 
                  ? 'rounded-none' 
                  : 'max-w-2xl max-h-[90vh] rounded-lg shadow-xl'
              }`}
              style={isMobile ? { minHeight: '100dvh', height: '100dvh', maxHeight: '100dvh', width: '100vw' } : {}}
                variants={modalVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                transition={transition}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className={`flex items-center justify-between border-b border-gray-200 dark:border-gray-700 flex-shrink-0 ${
                  isMobile ? 'px-4 py-4 pt-safe-top' : 'p-6'
                }`} style={isMobile ? { paddingTop: 'calc(env(safe-area-inset-top) + 1rem)' } : {}}>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Edit Profile</h2>
                  <button
                    onClick={closeModals}
                    className={`text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 ${
                      isMobile ? 'p-2 -mr-2' : ''
                    }`}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Form */}
                <form onSubmit={handleProfileFormSubmit} className="flex flex-col flex-1 overflow-hidden">
                  <div className={`flex-1 overflow-y-auto ${
                    isMobile ? 'px-4 py-4' : 'p-6'
                  }`} style={{ 
                    overscrollBehavior: 'contain', 
                    paddingBottom: isMobile ? 'calc(env(safe-area-inset-bottom, 0px) + 100px)' : 'calc(env(safe-area-inset-bottom, 0px) + 88px)' 
                  }}>
              {/* Avatar uploader */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Profile Photo
                </label>
                <div className="flex items-center gap-4">
                  <div className="h-16 w-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full overflow-hidden flex items-center justify-center">
                    {profileForm.avatar ? (
                      <img src={getImageUrl(profileForm.avatar)} alt="avatar" className="h-16 w-16 object-cover" />
                    ) : user?.avatar ? (
                      <img src={getImageUrl(user.avatar)} alt="avatar" className="h-16 w-16 object-cover" />
                    ) : (
                      <span className="text-white font-bold text-xl">{getInitials(user)}</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => avatarInputRef.current?.click()}
                      disabled={isUploadingAvatar}
                      className="px-3 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md disabled:opacity-50"
                    >
                      {isUploadingAvatar ? 'Uploading…' : 'Upload Image'}
                    </button>
                    {profileForm.avatar && (
                      <button
                        type="button"
                        onClick={() => setProfileForm(prev => ({ ...prev, avatar: '' }))}
                        className="px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md"
                      >
                        Remove
                      </button>
                    )}
                    <input
                      ref={avatarInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarFileChange}
                      className="hidden"
                    />
                  </div>
                </div>
                {profileForm.avatar && (
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">This image will be used as your profile photo.</p>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* First Name */}
                    <div>
                      <label htmlFor="profile-firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        First Name
                      </label>
                      <input
                        type="text"
                        id="profile-firstName"
                        value={profileForm.firstName || ''}
                        onChange={(e) => handleProfileInputChange('firstName', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter your first name"
                      />
                    </div>

                    {/* Last Name */}
                    <div>
                      <label htmlFor="profile-lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Last Name
                      </label>
                      <input
                        type="text"
                        id="profile-lastName"
                        value={profileForm.lastName || ''}
                        onChange={(e) => handleProfileInputChange('lastName', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter your last name"
                      />
                    </div>

                    {/* Email (read-only) */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Email
                      </label>
                      <div className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md text-gray-500 dark:text-gray-400">
                        {user?.email}
                      </div>
                    </div>

                    {/* Username (read-only) */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Username
                      </label>
                      <div className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md text-gray-500 dark:text-gray-400">
                        @{user?.username}
                      </div>
                    </div>
                  </div>

              {/* Interests */}
              <div className="mt-6">
                <label htmlFor="profile-interests" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Interests
                </label>
                <textarea
                  id="profile-interests"
                  rows={4}
                  value={profileForm.interests || ''}
                  onChange={(e) => handleProfileInputChange('interests', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Share your nature and outdoor interests..."
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Optional. This appears on your public profile.</p>
              </div>

              {/* Display Mode Preference */}
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Display Mode
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  Choose how the app appears on your device.
                </p>
                
                {/* Radio Button Group */}
                <div className="space-y-2">
                  {displayModeOptions.map((option) => (
                    <label
                      key={option.value}
                      className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                        displayMode === option.value
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="displayMode"
                        value={option.value}
                        checked={displayMode === option.value}
                        onChange={(e) => setDisplayMode(e.target.value)}
                        className="sr-only"
                      />
                      <div className={`flex-shrink-0 ${
                        displayMode === option.value
                          ? 'text-blue-600 dark:text-blue-400'
                          : 'text-gray-500 dark:text-gray-400'
                      }`}>
                        {option.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-medium ${
                          displayMode === option.value
                            ? 'text-blue-900 dark:text-blue-100'
                            : 'text-gray-900 dark:text-white'
                        }`}>
                          {option.label}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {option.description}
                        </div>
                      </div>
                      {displayMode === option.value && (
                        <svg className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </label>
                  ))}
                </div>
              </div>

              {/* Image Style Preference */}
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  AI Image Style Preference
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  Choose the visual style for AI-generated instruction images in your projects.
                </p>
                
                {/* Custom Dropdown */}
                <div className="relative" ref={imageStyleDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setIsImageStyleDropdownOpen(!isImageStyleDropdownOpen)}
                    className="w-full flex items-center gap-3 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <span className="text-lg">{getCurrentImageStyleOption().preview}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {getCurrentImageStyleOption().label}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {getCurrentImageStyleOption().description}
                      </div>
                    </div>
                    <svg className={`h-5 w-5 text-gray-400 transition-transform ${isImageStyleDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Dropdown Menu */}
                  {isImageStyleDropdownOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto">
                      {imageStyleOptions.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => {
                            handleAppSettingsChange('image_style', option.value);
                            setIsImageStyleDropdownOpen(false);
                          }}
                          className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-600 focus:outline-none focus:bg-gray-100 dark:focus:bg-gray-600"
                        >
                          <span className="text-lg">{option.preview}</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {option.label}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                              {option.description}
                            </div>
                          </div>
                          {(profileForm.app_settings?.image_style || 'IKEA-style line drawings with simple black outlines') === option.value && (
                            <svg className="h-4 w-4 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

                  </div>

                  {/* Footer */}
                  <div className={`flex items-center justify-end gap-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 ${
                    isMobile ? 'fixed inset-x-0 bottom-0 z-[60002] px-4 py-4' : 'p-6'
                  }`} style={isMobile ? { 
                    paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)', 
                    boxShadow: '0 -8px 16px rgba(0,0,0,0.06)' 
                  } : {}}>
                    <button
                      type="button"
                      onClick={closeModals}
                      className={`font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        isMobile ? 'px-6 py-3 text-base' : 'px-4 py-2 text-sm'
                      }`}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={updateProfileMutation.isPending}
                      className={`font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed ${
                        isMobile ? 'px-6 py-3 text-base' : 'px-4 py-2 text-sm'
                      }`}
                    >
                      {updateProfileMutation.isPending ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Saved List Modal */}
        <SavedListModal
          isOpen={listModal.isOpen}
          onClose={closeListModal}
          list={listModal.list}
          mode={listModal.mode}
        />

        </div>
      </div>
    </div>
  );
};

export default UserProfile; 